import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { householdsApi, formatError } from "../lib/api";
import { getHouseholdIcon } from "../lib/householdIcons";

/**
 * HouseholdSwitcherSheet — bottom-sheet/modal that lists every household the
 * current user is a member of and lets them tap one to make it active.
 *
 * Lighter than MultiHouseholdPanel: this component does ONLY the switch
 * action (no create / join / leave). Used by the Dashboard's "Active
 * Household" chip so multi-household users can hop between fridges
 * without leaving the home screen.
 *
 * Caller wires `open` / `onClose` and provides `onSwitched` so the page
 * can refresh after a successful switch.
 */
export const HouseholdSwitcherSheet = ({ open, onClose, activeHouseholdId, onSwitched }) => {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch all households whenever the sheet opens — keeps member counts
  // and active flag fresh in case the user just changed something
  // elsewhere in the app.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    householdsApi
      .all()
      .then((r) => { if (!cancelled) setList(r.households || []); })
      .catch(() => { if (!cancelled) setList([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const doSwitch = async (hid) => {
    if (hid === activeHouseholdId) { onClose?.(); return; }
    setBusy(true);
    try {
      const r = await householdsApi.switch(hid);
      // If the parent has wired `onSwitched`, it owns the full UX (e.g. the
      // Dashboard's blur+house animation). Skip the toast so the user gets
      // one coherent transition instead of competing UI noise.
      if (onSwitched) {
        onSwitched(r);
      } else {
        toast.success(`Switched to ${r.name}`);
        onClose?.();
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => !busy && onClose?.()}
          className="fixed inset-0 z-[2147483600] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center"
          data-testid="dashboard-switcher-overlay"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[480px] bg-[#FDFBF7] rounded-t-3xl sm:rounded-3xl p-5 sm:m-4 max-h-[78vh] overflow-y-auto"
            data-testid="dashboard-switcher-sheet"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading text-lg font-semibold text-[#2D3142]">
                Switch household
              </div>
              <button
                type="button"
                onClick={() => !busy && onClose?.()}
                data-testid="dashboard-switcher-close"
                aria-label="Close"
                className="w-9 h-9 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {loading ? (
              <div className="tup-card p-6 text-center text-sm text-[#8A93A6]">Loading…</div>
            ) : (
              <ul className="space-y-2" data-testid="dashboard-switcher-list">
                {list.map((h) => {
                  const RowIcon = getHouseholdIcon(h.icon);
                  return (
                  <li key={h.id}>
                    <button
                      type="button"
                      onClick={() => doSwitch(h.id)}
                      disabled={busy}
                      data-testid={`dashboard-switcher-option-${h.id}`}
                      className={`w-full text-left tup-card p-4 flex items-center justify-between gap-3 border-2 transition-colors disabled:opacity-60 ${
                        h.is_active
                          ? "border-[#277A82] bg-[#DCEDF0]/50"
                          : "border-transparent hover:border-[#277A82]/40 hover:bg-[#F5F3ED]"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                            h.is_active ? "bg-[#1F5566] text-white" : "bg-[#F1EEE5] text-[#4F5D75]"
                          }`}
                        >
                          <RowIcon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-sm text-[#2D3142] truncate">
                            {h.name}
                          </div>
                          <div className="text-[10px] text-[#8A93A6]">
                            {h.members.length} member{h.members.length === 1 ? "" : "s"}
                          </div>
                        </div>
                      </div>
                      {h.is_active && <Check className="w-4 h-4 text-[#1F5566] shrink-0" />}
                    </button>
                  </li>
                  );
                })}
              </ul>
            )}

            {busy && (
              <div className="mt-3 flex items-center justify-center gap-2 text-xs text-[#4F5D75]">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Switching…
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default HouseholdSwitcherSheet;
